import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, catchError, take, tap, throwError } from 'rxjs';

import {
  EntityCfg,
  EntityInterface,
  FilterableColCfg,
  makeTranslatedFilterCfgMap
} from '@gfe/core/models/entities/entity-visualization-interface';
import {
  EntitySelectorRequestModel,
  OrderBy,
  PlanFilterOperator
} from '@gfe/core/models/entity/entity-selector-request.model';
import { PagedList } from '@gfe/core/models/paged-list';
import { isNil } from '@gfe/core/utils/shared-utils';
import { FilterConfiguration } from '@gfe/shared/components/filter-query-builder/filter.models';
import { SearchResult } from '@gfe/shared/components/search-query-builder/search-query-builder.component';
import { Pagination } from '@gfe/shared/utils/pagination.utils';
import {
  convertSearchResultToFilterConfiguration,
  mergeFilterConfigurations
} from '@gfe/shared/utils/search-filter.utils';

import { FilterExpr } from '@core/filters/path-filter/wire';

import { EntityLabelsTranslationService } from '../entity-labels-translation/entity-labels-translation.service';
import { SelectorsService } from '../selectors/selectors.service';
import { UserModelsRegistryService } from '../user-models-registry/user-models-registry.service';

export interface FetchEntityRequest {
  entityType: string;
  entityVersionName: string;
  dataSpaceId: number;
  orderBy?: OrderBy[];
  plan?: {
    id: string;
    context: 'visualization' | 'edition';
  };
  searchResult?: SearchResult;
  filterConfiguration?: FilterConfiguration;
  /**
   * Set in Path mode. The request then carries this tree and free text and no classic
   * `filters`, whatever `searchResult` and `filterConfiguration` hold.
   */
  path?: { filter?: FilterExpr; search?: string };
}

interface EntityPaginationState {
  page: number;
  take: number;
  total: number;
  numPages: number;
}

const INITIAL_PAGE_STATE: EntityPaginationState = {
  page: 1,
  take: 100,
  total: 0,
  numPages: 0
};

@Injectable()
export class PagedEntityService {
  paginationState = computed(() => {
    return this.pagination();
  });
  private pagination = signal<EntityPaginationState>({ ...INITIAL_PAGE_STATE });
  private readonly selectorsService = inject(SelectorsService);
  private readonly userModelsService = inject(UserModelsRegistryService);
  private readonly entityLabelsTranslationService = inject(EntityLabelsTranslationService);
  private lastFetchRequest: EntitySelectorRequestModel | undefined;

  fetchEntities<T>(data: FetchEntityRequest): Observable<PagedList<EntityInterface<T>>> {
    const operator: PlanFilterOperator =
      data.plan?.context === 'visualization'
        ? PlanFilterOperator.IN
        : PlanFilterOperator.NOT_IN;

    const planFilter = !isNil(data.plan)
      ? {
          id: String(data.plan.id),
          operator
        }
      : undefined;

    const request: EntitySelectorRequestModel = isNil(data.path)
      ? this.classicRequest(data, planFilter)
      : {
          entityType: data.entityType.toUpperCase(),
          dataSpaceId: data.dataSpaceId,
          entityTypeVersion: data.entityVersionName,
          planFilter,
          search: data.path.search?.trim() || undefined,
          orderBy: data.orderBy,
          ...(isNil(data.path.filter) ? {} : { pathFilter: data.path.filter })
        };

    // Ask for summary if entity can omit fields
    if ((this.getEntityCfg<T>(data.entityVersionName)?.lazyFields?.() ?? []).length > 0) {
      request.view = 'SUMMARY';
    }

    this.lastFetchRequest = request;
    return this.getEntityData<T>(request);
  }

  private classicRequest(
    data: FetchEntityRequest,
    planFilter: EntitySelectorRequestModel['planFilter']
  ): EntitySelectorRequestModel {
    // The search bar's qualifiers become filter conditions, merged with the filter builder's.
    const searchBarFilterConfig = data.searchResult
      ? convertSearchResultToFilterConfiguration(
          data.searchResult,
          this.getTranslatedFilterableColMap(data.entityVersionName)
        )
      : undefined;
    return {
      entityType: data.entityType.toUpperCase(),
      dataSpaceId: data.dataSpaceId,
      entityTypeVersion: data.entityVersionName,
      planFilter,
      filters: mergeFilterConfigurations(searchBarFilterConfig, data.filterConfiguration),
      search: data.searchResult?.textTerms.join(' ').trim() || undefined,
      orderBy: data.orderBy
    };
  }

  getNextPage<T>(): Observable<PagedList<EntityInterface<T>>> {
    return this.goToPage<T>(this.pagination().page + 1);
  }

  getPreviousPage<T>(): Observable<PagedList<EntityInterface<T>>> {
    return this.goToPage<T>(this.pagination().page - 1);
  }

  resetPagination(): void {
    this.pagination.set({ ...INITIAL_PAGE_STATE });
  }

  goToPage<T>(pageNumber: number): Observable<PagedList<EntityInterface<T>>> {
    if (isNil(this.lastFetchRequest)) {
      throw new Error('No current request');
    }
    this.pagination.update((p) => ({ ...p, page: pageNumber }));
    return this.getEntityData<T>(this.lastFetchRequest);
  }

  getLastRequest(): EntitySelectorRequestModel | undefined {
    return this.lastFetchRequest;
  }

  private updatePageState(res: PagedList<EntityInterface<unknown>>): void {
    const take = Math.abs(res.take);
    // A fresh object: mutating the current one in place would leave the signal's identity
    // unchanged, so computeds reading the total would never recompute.
    this.pagination.set({
      page: res.page,
      take,
      total: res.total,
      numPages: Pagination.getTotalPages(res.total, take)
    });
  }

  private getEntityCfg<T>(entityVersionName?: string): EntityCfg<T> | undefined {
    if (isNil(entityVersionName)) {
      return undefined;
    }
    return this.userModelsService.getEntityCfg<T>(entityVersionName);
  }

  private getTranslatedFilterableColMap<T>(
    entityVersionName?: string
  ): Record<string, FilterableColCfg> {
    const tableCfg = this.getEntityCfg<T>(entityVersionName);
    return isNil(tableCfg)
      ? {}
      : makeTranslatedFilterCfgMap(tableCfg, this.entityLabelsTranslationService);
  }

  private getEntityData<T>(
    request: EntitySelectorRequestModel
  ): Observable<PagedList<EntityInterface<T>>> {
    const pagination = this.pagination();
    return this.selectorsService
      .selectDataParsedToEntityInterface$<T>(request, pagination.page, pagination.take)
      .pipe(
        take(1),
        catchError((err) => {
          this.resetPagination();
          return throwError(() => err);
        }),
        tap((res) => {
          this.updatePageState(res);
        })
      );
  }
}
